import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

interface ValidationWarningsProps {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  showSuccess?: boolean;
  className?: string;
}

const ValidationWarnings: React.FC<ValidationWarningsProps> = ({
  errors,
  warnings,
  showSuccess = false,
  className,
}) => {
  if (errors.length === 0 && warnings.length === 0) {
    if (showSuccess) {
      return (
        <Alert className={cn('border-success/30 bg-success/10', className)}>
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertTitle className="text-success-foreground">Ready to send</AlertTitle>
          <AlertDescription className="text-success-foreground/80">
            All required information is complete.
          </AlertDescription>
        </Alert>
      );
    }
    return null;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Required before sending</AlertTitle>
          <AlertDescription>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {errors.map((error, index) => (
                <li key={`error-${index}`}>{error.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert className="border-warning/30 bg-warning/10">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning-foreground">Review recommended</AlertTitle>
          <AlertDescription className="text-warning-foreground/80">
            <ul className="list-disc list-inside mt-2 space-y-1">
              {warnings.map((warning, index) => (
                <li key={`warning-${index}`}>{warning.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ValidationWarnings;
